import { Chip, MantineSize } from "@mantine/core";
import {
  CalculateRounded,
  ImportContactsRounded,
  SvgIconComponent,
} from "@mui/icons-material";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ChecklistRounded from "@mui/icons-material/ChecklistRounded";
import FolderRounded from "@mui/icons-material/FolderRounded";
import FolderSpecialRounded from "@mui/icons-material/FolderSpecialRounded";
import SentimentNeutralRounded from "@mui/icons-material/SentimentNeutralRounded";
import SentimentVeryDissatisfiedRounded from "@mui/icons-material/SentimentVeryDissatisfiedRounded";
import SentimentVerySatisfiedRounded from "@mui/icons-material/SentimentVerySatisfiedRounded";
import clsx from "clsx";
import classes from "./FilterChip.module.css";

// Predefined labels that have built-in icons
type PredefinedLabel =
  | "Easy"
  | "Medium"
  | "Hard"
  | "MC"
  | "LA"
  | "Excellent"
  | "Good"
  | "Poor"
  | "Calc"
  | "Main Bank"
  | "Test Bank"
  | "HL"
  | "SL";

export default function FilterChip({
  label,
  value,
  size = "md",
  checked,
  forceChecked,
  icon,
}: {
  label: string;
  value?: string;
  size?: MantineSize;
  checked?: boolean;
  forceChecked?: true;
  icon?: SvgIconComponent;
}) {
  const chipValue = value ?? label;
  const predefinedIcon = isPredefinedLabel(label)
    ? getIconFromValue(
        label,
        clsx(classes.icon, checked && classes.checkedIcon)
      )
    : null;
  const CustomIcon = icon;

  return (
    <Chip
      variant={"filled"}
      value={chipValue}
      color={"darkblue"}
      radius="md"
      size={size}
      checked={forceChecked ? true : undefined}
      classNames={{
        label: `${classes.chipLabel} filter-chip-label`,
        iconWrapper: classes.chipIconWrapper,
        checkIcon: classes.chipCheckIcon,
        root: classes.chipRoot,
      }}
      styles={(theme) => getChipStyles(theme, size, checked, forceChecked)}
    >
      <div className={classes.chipContent}>
        {CustomIcon ? (
          <CustomIcon
            style={{ width: 20, height: 20 }}
            className={clsx(classes.icon, checked && classes.checkedIcon)}
          />
        ) : (
          predefinedIcon
        )}
        <span>{label}</span>
      </div>
    </Chip>
  );
}

function isPredefinedLabel(label: string): label is PredefinedLabel {
  return [
    "Easy",
    "Medium",
    "Hard",
    "MC",
    "LA",
    "Excellent",
    "Good",
    "Poor",
    "Calc",
    "Main Bank",
    "Test Bank",
    "HL",
    "SL",
  ].includes(label);
}

function getChipStyles(
  theme: any,
  size?: MantineSize,
  checked?: boolean,
  forceChecked?: true
) {
  return {
    label: {
      minWidth: size === "xs" ? undefined : "auto !important",
      padding:
        size === "xs"
          ? "0.5rem 0.875rem !important"
          : "0.625rem 1rem !important",
      fontSize: size === "xs" ? theme.fontSizes.sm : theme.fontSizes.md,
      borderWidth: checked ? "0" : "1.5px",
    },
    root: {
      pointerEvents: forceChecked ? ("none" as const) : ("auto" as const),
    },
  };
}

function getIconFromValue(value: PredefinedLabel, iconClassName: string) {
  let Icon = CheckCircleRounded;
  switch (value) {
    case "Easy":
    case "Excellent":
      Icon = SentimentVerySatisfiedRounded;
      break;
    case "Medium":
    case "Good":
      Icon = SentimentNeutralRounded;
      break;
    case "Hard":
    case "Poor":
      Icon = SentimentVeryDissatisfiedRounded;
      break;
    case "MC":
      Icon = CheckCircleRounded;
      break;
    case "LA":
      Icon = ChecklistRounded;
      break;
    case "Calc":
      Icon = CalculateRounded;
      break;
    case "Main Bank":
      Icon = FolderRounded;
      break;
    case "Test Bank":
      Icon = FolderSpecialRounded;
      break;
    case "HL":
    case "SL":
      Icon = ImportContactsRounded;
      break;
  }
  return (
    <Icon
      style={{
        width: 20,
        height: 20,
      }}
      className={iconClassName}
    />
  );
}
